import type { ImportCounts, ImportPlan } from '../../app/backupImport';

/**
 * B-19 — the **More tab's pure decisions** (design spec §2.4's More row, §2.6, scenario 35).
 *
 * DOM-free, so the Node suite drives every arm; `MoreScreen` renders over these. Three kinds of
 * question live here, and none of them is settleable by a screenshot:
 *
 * 1. **What a pre-flight result actually means** (§2.6) — six outcomes, six sentences, each
 *    naming *where the fix is*. This is the pre-mortem's whole subject: a configuration failure
 *    worded as a network failure is how the flagship feature ships dead.
 * 2. **What may be shown about a stored credential** — the answer is "that there is one, and how
 *    long it is", and the type is what makes the rest unavailable rather than merely unused.
 * 3. **What an import is about to do** — BAK-4's confirm step, honest in both directions.
 */

// ── The credential, and what may be shown about it ───────────────────────────

/**
 * Everything the screen is allowed to know about a stored credential.
 *
 * **There is no field here that could carry the secret**, and that is the mechanism rather than a
 * convention: `MoreScreen` never puts the stored value in React state, so it cannot be rendered
 * into a value attribute, read out of the DOM, or serialised into a screenshot. `length` is the
 * one signal a user can act on ("did the whole thing paste?") that reveals nothing about the
 * characters — 44 characters is the same answer for every 44-character token.
 */
export interface CredentialStateB {
  readonly stored: boolean;
  /** Characters in the stored credential, trimmed. `0` when nothing is stored. */
  readonly length: number;
}

/**
 * Reads the stored setting as a credential state.
 *
 * A blank or whitespace-only value is **not stored** — the same reading `app/coach.readCredential`
 * takes before it declines to send, so the screen and the generate path can never disagree about
 * whether Coach Claude is set up.
 */
export function credentialStateB(value: unknown): CredentialStateB {
  const text = typeof value === 'string' ? value.trim() : '';
  return { stored: text !== '', length: text.length };
}

// ── Scenario 35 — the import plan, which IS the confirm step ─────────────────

/** One line of a plan preview: a thing the user recognises, and how many of it. */
export interface ImportLineB {
  readonly key: string;
  readonly label: string;
  readonly count: number;
}

export interface ImportSideB {
  readonly total: number;
  /** Non-zero lines only, biggest first, then alphabetically — a stable, readable order. */
  readonly lines: readonly ImportLineB[];
}

/**
 * The two device-local settings an import silently takes with it.
 *
 * `REPLACED_TABLES` includes `settings`, so the wipe empties it and the archive's own settings
 * refill it — and `backupCredential`/`themePreference` are denied in **both** directions
 * (`archive/fields.ts`), so neither can ever come back from a file. The consequence is real and
 * entirely non-obvious: **importing an archive signs this device out of Coach Claude.** A user
 * who discovers that at the next halftime has been ambushed by a correct implementation.
 */
export interface DeviceLocalB {
  readonly credentialStored: boolean;
  readonly themeStored: boolean;
}

/**
 * B spec §15.6 (build unit B-25) — what this device's **profile** holds that the picked file may
 * not put back.
 *
 * Separate from {@link DeviceLocalB} because the reason is different, and the difference is the
 * whole point. The credential and the theme are **denied in both directions**: no archive can ever
 * carry them, so the loss is unconditional and follows from the deny-list. The team shape is
 * `'archive'` — it round-trips perfectly through a file that has one. The loss happens only when
 * the picked file **doesn't** carry one, which is a property of the file rather than of the format.
 *
 * ── Why this needed saying at all ────────────────────────────────────────────
 *
 * `TABLE.settings` is in `REPLACED_TABLES` alongside `ourPlayers` (`repos/profile.ts:96-115`), and
 * the import wipes those tables and re-inserts inside one transaction. So importing **any** archive
 * that carries no `teamShape` — one written before the key existed, or one from a device where the
 * shape was never set — removes the current shape and restores nothing, **silently**. Absence is
 * the contract for a key that was never set (`settingsB.ts:27-29`), which is why nothing breaks;
 * it is also exactly why nothing warned. That is the *"half a profile"* outcome §15.4 uses to
 * justify the `'archive'` disposition, arriving from the other direction — and the fix is the
 * sentence, not the machinery, which already reports per-source.
 */
export interface ProfileNowB {
  /** True when a NON-EMPTY team shape is recorded on this device — something there is to lose. */
  readonly shapeStored: boolean;
  /** How many roster players an import would remove. `0` means the sentence has nothing to say. */
  readonly rosterCount: number;
}

export interface ImportSummaryB {
  /** What will be destroyed. `willRemove`, keyed by **table**. */
  readonly removing: ImportSideB;
  /** What will arrive. `willImport`, keyed by **archive entity**. */
  readonly importing: ImportSideB;
  /** Format versions of the picked files — how an older A export announces itself. */
  readonly formatVersions: readonly number[];
  /** Anything the plan found wrong that the user should read BEFORE confirming. */
  readonly warnings: readonly string[];
}

/**
 * User-facing names. Missing keys fall through to the raw key, which is honest rather than
 * wrong: a future entity shows up as `coachCards` instead of silently not showing up at all.
 *
 * The map covers both vocabularies because the two sides are summarised **independently** —
 * see {@link summariseImportB}.
 */
const LABELS: Readonly<Record<string, string>> = {
  teams: 'Opponents',
  players: 'Opponent players',
  matches: 'Matches',
  hypotheses: 'Patterns',
  observations: 'Recorded instances',
  flagResolutions: 'Flag decisions',
  setPieces: 'Set pieces',
  talkPoints: 'Talk points',
  matchNotes: 'Match notes',
  annotations: 'Annotations',
  coachCards: 'Coach cards',
  ourPlayers: 'My team',
  audioBlobs: 'Voice notes',
  templates: 'Pattern library',
  settings: 'Settings',
  logEntries: 'Debug log',
};

/**
 * The three tables that are device bookkeeping rather than anything the user made.
 *
 * They are folded into one line because their individual counts are **actively misleading**, not
 * merely uninteresting: `activeMatch` is a singleton whose `matchId` is usually `null`, so a row
 * labelled *Match in progress · 1* told a user with no match in progress that they had one
 * (found by reading this pass's screenshots). Folding keeps the lines adding up to the plan's
 * own total, which dropping them would not.
 */
const BOOKKEEPING: ReadonlySet<string> = new Set(['outbox', 'uiSession', 'activeMatch']);
const BOOKKEEPING_KEY = 'bookkeeping';

function toSide(counts: ImportCounts): ImportSideB {
  const lines: ImportLineB[] = [];
  let bookkeeping = 0;
  for (const [key, count] of Object.entries(counts.byEntity)) {
    if (count === undefined || count === 0) continue;
    if (BOOKKEEPING.has(key)) {
      bookkeeping += count;
      continue;
    }
    lines.push({ key, label: LABELS[key] ?? key, count });
  }
  if (bookkeeping > 0) {
    lines.push({ key: BOOKKEEPING_KEY, label: 'App bookkeeping', count: bookkeeping });
  }
  lines.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  return { total: counts.total, lines };
}

/**
 * BAK-4 / scenario 35 — what the plan is about to do, in the user's words.
 *
 * ── The two sides are summarised separately, on purpose ──────────────────────
 *
 * `app/backupImport` states the hazard outright: `willRemove` is keyed by **table name** (it
 * covers `outbox`, `uiSession` and `activeMatch`, none of which is an archive entity) and
 * `willImport` is keyed by **archive entity**. *"A confirm screen that indexed one with the
 * other's key would silently render a blank."* So this function never indexes across them — it
 * renders two independent lists rather than one paired table, and the failure it would otherwise
 * be exposed to cannot occur.
 *
 * Warnings are the honesty half. An import that quarantined a damaged match still succeeds, and
 * a user who confirms without being told has lost data they believed they were restoring — and
 * the same is true of {@link DeviceLocalB}, which no count on either side of this preview would
 * ever reveal.
 */
export function summariseImportB(
  plan: ImportPlan,
  deviceLocal: DeviceLocalB,
  profileNow: ProfileNowB,
): ImportSummaryB {
  const v = plan.validation;
  const warnings: string[] = [];

  if (v.quarantinedMatches.length > 0) {
    warnings.push(
      `${String(v.quarantinedMatches.length)} match${v.quarantinedMatches.length === 1 ? '' : 'es'} in this file are damaged and will NOT be imported.`,
    );
  }
  if (v.quarantinedRows.length > 0) {
    warnings.push(
      `${String(v.quarantinedRows.length)} row${v.quarantinedRows.length === 1 ? '' : 's'} failed validation and will be left out.`,
    );
  }
  if (v.blobBytesWithoutMetadata.length > 0) {
    warnings.push(
      `${String(v.blobBytesWithoutMetadata.length)} voice note${v.blobBytesWithoutMetadata.length === 1 ? '' : 's'} in this file cannot be restored.`,
    );
  }
  if (v.blobsRestoredEmpty.length > 0) {
    warnings.push(
      `${String(v.blobsRestoredEmpty.length)} voice note${v.blobsRestoredEmpty.length === 1 ? '' : 's'} will come back without their audio; the moment and its minute survive.`,
    );
  }

  if (deviceLocal.credentialStored) {
    warnings.push(
      'Your Coach Claude service credential is in no archive, so importing removes it from this device. You will need to paste it again afterwards.',
    );
  }
  if (deviceLocal.themeStored) {
    warnings.push(
      'Your theme choice is in no archive either, so it goes back to following this device’s own setting.',
    );
  }

  /**
   * §15.6 — the team shape's replace-all consequence, stated **before** the confirm.
   *
   * The condition is read off the rows that will actually be written, not off a format version: a
   * pre-`teamShape` archive and an archive from a device that never set a shape are the same event
   * to the user, and both are answered by "this file carries no shape". Reading the rows also means
   * the sentence cannot go stale the day another archive version ships.
   */
  const fileCarriesShape = v.rows.settings.some((row) => row.key === 'teamShape');
  if (profileNow.shapeStored && !fileCarriesShape) {
    warnings.push(
      'Your team shape will be removed and not restored — this backup doesn’t carry one. Write it down first if you want it back.',
    );
  }

  /**
   * §15.6 — the ROSTER's replace-all consequence, the shape's sibling above.
   *
   * `ourPlayers` is in `REPLACED_TABLES` and the import wipes inside the same transaction as the
   * inserts, so importing an archive that carries no roster **removes the current one and restores
   * nothing**. A pre-format-4 archive is the case that makes this urgent: §15.6's entity-grain drop
   * removes its legacy rows deliberately, so the file genuinely has none — and the preview must say
   * that in the user's terms, never as *"ourPlayers: dropped"*.
   *
   * Read off the rows that will actually be written, exactly as the shape's sentence is, so it
   * cannot go stale the day another archive version ships. The `droppedLegacy` half only chooses
   * the REASON clause; the warning itself fires on the consequence.
   */
  const droppedLegacyRoster = plan.sources.some((source) =>
    source.report.droppedLegacyEntities.includes('ourPlayers'),
  );
  if (profileNow.rosterCount > 0 && v.rows.ourPlayers.length === 0) {
    const players = `${String(profileNow.rosterCount)} roster player${profileNow.rosterCount === 1 ? '' : 's'}`;
    warnings.push(
      droppedLegacyRoster
        ? `Your ${players} will be removed and 0 restored — this backup predates the new team profile.`
        : `Your ${players} will be removed and 0 restored — this backup doesn’t carry a roster.`,
    );
  }

  return {
    removing: toSide(plan.willRemove),
    importing: toSide(plan.willImport),
    formatVersions: plan.sources.map((source) => source.formatVersion),
    warnings,
  };
}

// ── Small shared shapes ──────────────────────────────────────────────────────

/**
 * A byte count in the units a person uses. Binary units, because that is what a file listing
 * shows; one decimal place past a kilobyte, because "1.4 MB" is a size and "1.437 MB" is a
 * measurement.
 */
export function byteSizeB(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${String(Math.round(bytes))} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
